'use client'

import * as React from 'react'
import Link from 'next/link'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useConsent } from '@/lib/consent'
import type { ConsentCategories } from '@/lib/consent'

type CategoryKey = Exclude<keyof ConsentCategories, 'necessary'>

type CategoryDef = {
  key: 'necessary' | CategoryKey
  title: string
  body: string
  locked?: boolean
}

const CATEGORIES: CategoryDef[] = [
  {
    key: 'necessary',
    title: 'Strictly necessary',
    body: 'Required for the site to function, language preference, security and load balancing. Always on.',
    locked: true,
  },
  {
    key: 'functional',
    title: 'Functional (live chat, support tools)',
    body: 'Lets us run live chat and similar support tools so you can talk to a human while you browse. On by default.',
  },
  {
    key: 'analytics',
    title: 'Analytics',
    body: 'Aggregated traffic measurement so we know which pages help and which don’t. No profiling.',
  },
  {
    key: 'marketing',
    title: 'Marketing',
    body: 'Ad-platform pixels (e.g. LinkedIn, Meta) and attribution tags so we can measure campaign ROI.',
  },
  {
    key: 'personalization',
    title: 'Personalization',
    body: 'Remembers what you’ve seen so we can tailor case studies and recommendations to your industry.',
  },
]

function Toggle({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean
  disabled?: boolean
  onChange: (next: boolean) => void
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        checked ? 'border-primary bg-primary' : 'border-input bg-secondary',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      <span
        className={cn(
          'pointer-events-none absolute top-1/2 inline-block h-4 w-4 -translate-y-1/2 rounded-full bg-background shadow transition-transform',
          checked ? 'translate-x-[22px]' : 'translate-x-1',
        )}
      />
    </button>
  )
}

export function CookieSettingsModal() {
  const { settingsOpen, closeSettings, consent, acceptAll, rejectAll, updateConsent } = useConsent()

  const [draft, setDraft] = React.useState<Pick<ConsentCategories, CategoryKey>>({
    analytics: false,
    marketing: false,
    personalization: false,
    functional: true,
  })

  const dialogRef = React.useRef<HTMLDivElement | null>(null)
  const previouslyFocusedRef = React.useRef<HTMLElement | null>(null)

  React.useEffect(() => {
    if (!settingsOpen) return
    setDraft({
      analytics: consent?.analytics ?? false,
      marketing: consent?.marketing ?? false,
      personalization: consent?.personalization ?? false,
      // Functional defaults to ON in the modal, matches PRE_DECISION_DEFAULTS.
      functional: consent?.functional ?? true,
    })
  }, [settingsOpen, consent])

  React.useEffect(() => {
    if (!settingsOpen) return

    previouslyFocusedRef.current = (document.activeElement as HTMLElement) ?? null

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        closeSettings()
        return
      }
      if (e.key !== 'Tab') return
      const node = dialogRef.current
      if (!node) return
      const focusables = node.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled])',
      )
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const t = window.setTimeout(() => {
      const node = dialogRef.current
      if (!node) return
      const first = node.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      first?.focus()
    }, 0)

    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
      window.clearTimeout(t)
      previouslyFocusedRef.current?.focus?.()
    }
  }, [settingsOpen, closeSettings])

  if (!settingsOpen) return null

  function savePreferences() {
    updateConsent(draft)
    closeSettings()
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeSettings()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-settings-title"
        aria-describedby="cookie-settings-desc"
        className="relative flex w-full max-w-lg flex-col rounded-t-2xl border bg-card shadow-2xl sm:rounded-2xl"
      >
        <button
          type="button"
          onClick={closeSettings}
          aria-label="Close cookie settings"
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>

        <div className="border-b px-5 py-4 sm:px-6 sm:py-5">
          <h2
            id="cookie-settings-title"
            className="font-display text-lg font-semibold tracking-tight"
          >
            Cookie preferences
          </h2>
          <p id="cookie-settings-desc" className="mt-1 text-[13px] text-muted-foreground">
            Choose which categories of cookies we can set on this device. See the{' '}
            <Link
              href="/privacy-policy"
              className="underline-offset-4 hover:underline focus-visible:underline"
            >
              privacy policy
            </Link>{' '}
            for more.
          </p>
        </div>

        <div className="max-h-[60vh] divide-y overflow-y-auto px-5 sm:px-6">
          {CATEGORIES.map((cat) => {
            const isLocked = cat.locked === true
            const checked =
              cat.key === 'necessary'
                ? true
                : draft[cat.key as CategoryKey]
            return (
              <div key={cat.key} className="flex items-start gap-4 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold">{cat.title}</h3>
                    {isLocked && (
                      <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Always on
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{cat.body}</p>
                </div>
                <Toggle
                  checked={checked}
                  disabled={isLocked}
                  label={`${cat.title} cookies`}
                  onChange={(next) => {
                    if (isLocked) return
                    setDraft((d) => ({ ...d, [cat.key]: next }))
                  }}
                />
              </div>
            )
          })}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t bg-secondary/30 px-5 py-4 sm:px-6">
          <button
            type="button"
            onClick={rejectAll}
            className="inline-flex h-9 items-center rounded-lg border bg-background px-3 text-xs font-medium transition hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Reject all
          </button>
          <button
            type="button"
            onClick={acceptAll}
            className="inline-flex h-9 items-center rounded-lg border bg-background px-3 text-xs font-medium transition hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Accept all
          </button>
          <button
            type="button"
            onClick={savePreferences}
            className="inline-flex h-9 items-center rounded-lg bg-primary px-4 text-xs font-semibold text-primary-foreground shadow-sm shadow-primary/20 transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Save preferences
          </button>
        </div>
      </div>
    </div>
  )
}
